/**
 * (owner, Sep 16) Bin day. Every house puts its bin out once a week — the day is hashed per house — and the bin stands
 * at the curb beside the front door as a bulk pickup (`items.garbage` = the bags) that nobody lifts by hand: a garbage
 * truck stopped beside it loads the bags (`def.bags` fit), and the landfill outside town pays a bag when the truck stops
 * in its yard. A bin nobody collected by midnight goes back in with a week missed: next time it has a bag more (the smell
 * — flies over it, renderer), and the house's owner loses a karma point a bag. NPC trucks take bins left out for hours,
 * the way ambulances take bodies (services.ts). The state is small and lives with the game: `binsDone` (collected today)
 * and `binsMissed` (weeks missed per house); bins re-appear from it whenever their chunk is live.
 */
import { count as countAch } from './achievements';
import { CHUNK } from '../world/generator';
import type { PlacedBuilding } from '../world/buildings';
import { hash } from '../world/rng';
import { WorldStream } from '../engine/world-stream';
import { karma } from './animals';
import { newId, forward, type Pickup, type VehicleEntity } from './entities';
import type { Game, PlayerState } from './game';
import { doorstep } from './property';

/** days in a bin week */
export const BIN_WEEK = 7;
/** the most bags a bin holds (weeks missed + 1) */
export const BIN_MAX = 5;
/** seconds a bin stands before the city's own truck is sent for it (a player on the round gets there first) */
export const BIN_NPC_AFTER = 240;
/** seconds a truck takes over one bag (loading), and over the whole load (tipping) */
export const LOAD_S = 0.4, TIP_S = 1.5;

const HOMEY = new Set(['residential', 'house', 'cabin', 'villa', 'cottage', 'apartment', 'apartments', 'flat', 'flats', 'condo', 'townhouse', 'duplex', 'row_house', 'loft', 'tiny_house', 'farm']);

/** a house that puts a bin out: somewhere people live, standing (not rubble, not a pad) */
export function binHouse(b: PlacedBuilding): boolean { return b.height > 0 && !b.ruined && b.def.tags.some((t) => HOMEY.has(t)); }

/** which day of the week (0..6) this house's bin goes out */
export function binDay(b: PlacedBuilding, seed: number): number { return hash(seed, b.x, b.y, 4711) % BIN_WEEK; }

/** where the bin stands: a step and a half along the front from the doorstep, at the curb side of the door */
export function binSpot(b: PlacedBuilding, seed: number): { x: number; y: number } {
  const d = doorstep(b, seed);
  return b.rot === 0 || b.rot === 2 ? { x: d.x + 1.6, y: d.y } : { x: d.x, y: d.y + 1.6 };
}

/** is this vehicle a garbage truck (carries bags) */
export function isGarbageTruck(v: VehicleEntity): boolean { return (v.def.bags ?? 0) > 0; }

/** the bin pickups out right now */
export function bins(game: Game): Pickup[] { return game.pickups.filter((k) => k.bin !== undefined); }

/**
 * Once a second: every live house whose day it is and whose bin was not collected today has its bin out — with a bag for
 * every week missed on top of this week's. (A bin that went with its chunk comes back the same when the chunk does.)
 */
export function stepBins(game: Game): void {
  const today = game.day % BIN_WEEK, out = new Set<string>();
  for (const k of game.pickups) if (k.bin !== undefined) out.add(k.bin);
  for (const lc of game.world.liveChunks()) {
    for (const b of lc.chunk.buildings) {
      if (!binHouse(b) || binDay(b, game.seed) !== today) continue;
      const key = `${b.x},${b.y}`;
      if (out.has(key) || game.binsDone.has(key)) continue;
      const s = binSpot(b, game.seed);
      const bags = Math.min(BIN_MAX, 1 + (game.binsMissed.get(key) ?? 0));
      game.pickups.push({ id: newId(), x: s.x, y: s.y, weaponId: 'items', ammo: 0, home: WorldStream.key(Math.floor(s.x / CHUNK), Math.floor(s.y / CHUNK)), t: 0, items: { garbage: bags }, bulk: true, bin: key });
    }
  }
}

/** midnight: whatever is still out goes back in with a week missed (its owner hears of the smell); the day's book is closed */
export function rollBins(game: Game): void {
  for (let i = game.pickups.length - 1; i >= 0; i--) {
    const k = game.pickups[i]; if (k.bin === undefined) continue;
    const missed = Math.min(BIN_MAX - 1, (game.binsMissed.get(k.bin) ?? 0) + 1);
    game.binsMissed.set(k.bin, missed);
    const prop = game.props.get(k.bin), owner = prop ? game.ownerOnline(prop) : undefined;
    if (owner) karma(game, owner, -(k.items?.garbage ?? 1), `the bins at ${game.world.buildingAt(...(k.bin.split(',').map((n) => Number(n) + 0.5) as [number, number]))?.def.name ?? 'your place'} were left out — ${missed} week${missed === 1 ? '' : 's'} missed, the neighbours smell it`);
    game.pickups.splice(i, 1);
  }
  game.binsDone.clear();
}

/** a bin taken (by anyone): off the street, the book says collected, the house's missed weeks are forgiven */
export function collectBin(game: Game, k: Pickup): void {
  const i = game.pickups.indexOf(k); if (i >= 0) game.pickups.splice(i, 1);
  if (k.bin !== undefined) { game.binsDone.add(k.bin); game.binsMissed.delete(k.bin); }
}

/** the landfill whose yard this is: the building tagged `landfill` within eighteen tiles */
export function landfillNear(game: Game, x: number, y: number): PlacedBuilding | undefined {
  for (const lc of game.world.liveChunks()) for (const b of lc.chunk.buildings) if (b.def.tags.includes('landfill') && x > b.x - 18 && x < b.x + b.w + 18 && y > b.y - 18 && y < b.y + b.h + 18) return b;
  return undefined;
}

/**
 * At the wheel of a garbage truck, nearly stopped: a bin within reach of the back loads a bag every LOAD_S until the truck
 * is full; in the landfill's yard with a load, TIP_S later the load is tipped and paid (item garbage's value a bag) — on
 * the job or not (the owner: "player gets money when they take the garbage to landfill, pay by amount").
 */
export function stepGarbage(game: Game, st: PlayerState, dt: number): void {
  const p = st.person, v = p.vehicle; if (!v || v.driver !== p || v.wrecked || !isGarbageTruck(v)) return;
  const cap = v.def.bags!, bags = v.bags ?? 0;
  if (Math.abs(v.speed) >= 1) { st.logT = 0; st.millT = 0; return; }
  const [fx, fy] = forward(v.angle), hl = v.def.size[1] / 2, bx = v.x - fx * hl * 0.6, by = v.y - fy * hl * 0.6; // (the hopper at the back)
  if (bags < cap) {
    const k = game.pickups.find((q) => q.bin !== undefined && Math.hypot(q.x - bx, q.y - by) < hl * 0.6 + 3.2);
    if (k) {
      st.logT = (st.logT ?? 0) + dt;
      if (st.logT >= LOAD_S) {
        st.logT = 0; v.bags = bags + 1; k.items!.garbage = (k.items!.garbage ?? 1) - 1;
        game.events.push({ kind: 'impact', x: bx, y: by, strength: 0.6, cls: 'wood' });
        if ((k.items!.garbage ?? 0) <= 0) { collectBin(game, k); if (v.bags >= cap) game.say(`Full — ${cap} bags; tip them at the landfill`, p); else if (!game.pickups.some((q) => q.bin !== undefined && Math.hypot(q.x - bx, q.y - by) < hl * 0.6 + 3.2)) game.say(`Bin emptied — ${v.bags} of ${cap} bags aboard`, p); }
      }
    } else st.logT = 0;
  }
  if (bags > 0 && Math.abs(v.speed) < 0.5) {
    const lf = landfillNear(game, v.x, v.y);
    if (lf) {
      st.millT = (st.millT ?? 0) + dt;
      if (st.millT >= TIP_S) {
        st.millT = 0; const each = game.reg.get('item', 'garbage')?.value ?? 9, pay = bags * each, before = p.items.cash ?? 0;
        p.items.cash = before + pay; v.bags = 0; game.teamShare(st, pay, 'sale');
        countAch(game, st, 'bags', bags);
        game.events.push({ kind: 'cash', x: p.x, y: p.y, cls: 'in' });
        game.say(`${lf.def.name}: ${bags} bag${bags === 1 ? '' : 's'} tipped — $${pay}`, p);
      }
    } else st.millT = 0;
  } else st.millT = 0;
}
